import { useState, useEffect, useMemo } from "react";
import { useServicesStore } from "@/lib/stores/services";
import { Service } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plus, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ServiceTable } from "./services/ServiceTable";
import { ServiceForm } from "./services/ServiceForm";
import { DeleteServiceDialog } from "./services/DeleteServiceDialog";
import { EmptyServicesList } from "./services/EmptyServicesList";

export function ServicesManagement() {
  const { services, isLoading, error, fetchServices, addService, updateService, removeService } = useServicesStore();
  const { toast } = useToast();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentService, setCurrentService] = useState<Partial<Service>>({
    name: "",
    description: "",
    price: 0,
    category: "",
    active: true,
  });

  const [serviceToDelete, setServiceToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // Filter services based on search query
  const filteredServices = useMemo(() => {
    if (!searchQuery.trim()) {
      return services;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return services.filter((service) =>
      service.name.toLowerCase().includes(query) ||
      service.category.toLowerCase().includes(query) ||
      service.description?.toLowerCase().includes(query) ||
      service.price.toString().includes(query)
    );
  }, [services, searchQuery]);

  const handleOpenAddDialog = () => {
    setCurrentService({
      name: "",
      description: "",
      price: 0,
      category: "",
      active: true,
    });
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (service: Service) => {
    setCurrentService(service);
    setIsDialogOpen(true);
  };

  const handleOpenDeleteDialog = (serviceId: string) => {
    setServiceToDelete(serviceId);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveService = async (service: Partial<Service>) => {
    try {
      if (service.id) {
        // Update existing service
        await updateService(service as Service);
        toast({
          title: "Service updated",
          description: `${service.name} has been updated successfully.`,
        });
      } else {
        // Add new service
        await addService(service as Omit<Service, "id">);
        toast({
          title: "Service added",
          description: `${service.name} has been added successfully.`,
        });
      }
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save service. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteService = async () => {
    if (serviceToDelete) {
      try {
        await removeService(serviceToDelete);
        toast({
          title: "Service removed",
          description: "The service has been removed successfully.",
        });
        setIsDeleteDialogOpen(false);
        setServiceToDelete(null);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete service. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Services Management</CardTitle>
            <CardDescription>
              Manage your clinic's services and pricing
            </CardDescription>
          </div>
          
          {/* Search Box */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search services by name, category, description, or price..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex items-center gap-4">
            {searchQuery && (
              <p className="text-sm text-gray-600 whitespace-nowrap">
                Showing {filteredServices.length} of {services.length} services
              </p>
            )}
            <Button onClick={handleOpenAddDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Add Service
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>

        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            Loading services...
          </div>
        ) : filteredServices.length > 0 ? (
          <ServiceTable 
            services={filteredServices} 
            onEdit={handleOpenEditDialog} 
            onDelete={handleOpenDeleteDialog} 
          />
        ) : searchQuery ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No services found matching "{searchQuery}"</p>
            <Button variant="outline" onClick={() => setSearchQuery("")}>
              Clear Search
            </Button>
          </div>
        ) : (
          <EmptyServicesList onAddService={handleOpenAddDialog} />
        )}

        <ServiceForm 
          isOpen={isDialogOpen} 
          setIsOpen={setIsDialogOpen} 
          currentService={currentService} 
          onSave={handleSaveService} 
        />

        <DeleteServiceDialog 
          isOpen={isDeleteDialogOpen} 
          setIsOpen={setIsDeleteDialogOpen} 
          onDelete={handleDeleteService} 
        />
      </CardContent>
    </Card>
  );
}
